'use client';

/**
 * CommandPalette — a global ⌘K / Ctrl+K command launcher. Composes the existing
 * cmdk primitives (CommandDialog gives fuzzy filtering + arrow-key nav + Enter
 * to run for free); this adds the global keybinding, group bucketing, and a
 * controlled-or-self-managed open state via `useCommandPalette`.
 *
 * The sibling `command.tsx` exports the raw cmdk building blocks (Command,
 * CommandItem, …); THIS is the assembled, keybinding-wired palette that takes a
 * flat `PaletteCommand[]` list (named to avoid colliding with cmdk's `Command`
 * export in the shared barrel). Keep the two distinct — primitives vs. composed
 * widget, the same way `combobox.tsx` composes those primitives into a select.
 */
import * as React from 'react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from './command';

export interface PaletteCommand {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
  readonly group?: string;
  readonly run: () => void;
}

/**
 * Open-state controller for the palette. Self-manages `open` and binds the
 * global ⌘K / Ctrl+K shortcut (toggles the palette). Pass the returned
 * `{ open, setOpen }` to <CommandPalette/>, or wire your own toolbar button to
 * `setOpen(true)`.
 */
export function useCommandPalette(): {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
} {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (typeof e.key === 'string' && e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return { open, setOpen };
}

export interface CommandPaletteProps {
  readonly commands: readonly PaletteCommand[];
  /** Controlled open state (e.g. from `useCommandPalette`). Omit to let the palette self-manage + bind ⌘K. */
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  readonly placeholder?: string;
  readonly emptyText?: string;
}

export function CommandPalette({
  commands,
  open: openProp,
  onOpenChange,
  placeholder = 'Type a command or search…',
  emptyText = 'No commands found.',
}: CommandPaletteProps) {
  // Self-managed mode (no `open` prop): own the state + bind ⌘K here so a bare
  // <CommandPalette commands={…}/> is a complete, keybinding-wired launcher.
  const fallback = useCommandPalette();
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : fallback.open;
  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) fallback.setOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, fallback, onOpenChange],
  );

  // Bucket commands by group, preserving first-seen group order.
  const groups = React.useMemo(() => {
    const order: string[] = [];
    const byGroup = new Map<string, PaletteCommand[]>();
    for (const cmd of commands) {
      const key = cmd.group ?? '';
      if (!byGroup.has(key)) {
        byGroup.set(key, []);
        order.push(key);
      }
      byGroup.get(key)!.push(cmd);
    }
    return order.map((key) => ({ key, items: byGroup.get(key)! }));
  }, [commands]);

  const runCommand = (cmd: PaletteCommand) => {
    setOpen(false);
    cmd.run();
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder={placeholder} />
      <CommandList>
        <CommandEmpty>{emptyText}</CommandEmpty>
        {groups.map((group, gi) => (
          <React.Fragment key={group.key || `__ungrouped-${gi}`}>
            {gi > 0 && <CommandSeparator />}
            <CommandGroup {...(group.key ? { heading: group.key } : {})}>
              {group.items.map((cmd) => (
                <CommandItem key={cmd.id} value={`${cmd.label} ${cmd.hint ?? ''}`} onSelect={() => runCommand(cmd)}>
                  <span className="flex-1 truncate">{cmd.label}</span>
                  {cmd.hint && <CommandShortcut>{cmd.hint}</CommandShortcut>}
                </CommandItem>
              ))}
            </CommandGroup>
          </React.Fragment>
        ))}
      </CommandList>
    </CommandDialog>
  );
}
